import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { TailSpin } from "react-loader-spinner";
import { BiExport } from "react-icons/bi";

import AdminTransactionsExportModal from "../../components/admin-export/AdminTransactionsExportModal";
import { DataTable } from "../../components/data-table";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { PROD_LINK, STAGING_LINK } from "../../constant";
import { IS_STAGING } from "../../constant/api";
import type { AdminSpreadTransaction } from "../../lib/api/adminTransactionsData";
import { useAdminSpreadSummariesQuery } from "../../lib/api/useAdminSpreadSummariesQuery";
import { useAdminSpreadTransactionsDataQuery } from "../../lib/api/useAdminSpreadTransactionsDataQuery";
import { formatAmount } from "../../utils/FormatPerson";
import AdminFeeDetailModal from "./AdminFeeDetailModal";
import {
  FEES_SWAP_STATUS_FILTERS,
  FEES_TABLE_LIMIT,
  type FeesSwapStatusFilterValue,
  type FeesTableRow,
  buildFeesTableRows,
  createFeesTableColumns,
  feesFlowLabel,
  swapStatusBadgeClass,
} from "./feesTransactionsTable";

/** Fixed display order for swap statuses; unknown keys follow, by count. */
const SWAP_STATUS_ORDER = ["SWAPPED", "PROCESSING", "PENDING", "FAILED"];

function apiErrorMessage(err: unknown, fallback: string): string {
  if (
    axios.isAxiosError(err) &&
    err.response?.data &&
    typeof err.response.data === "object" &&
    "message" in err.response.data
  ) {
    return String((err.response.data as { message?: string }).message);
  }
  return fallback;
}

function trxValue(v: number | null | undefined): string {
  return v == null ? "—" : `${formatAmount(v)} TRX`;
}

const FeesOverview: React.FC = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tablePage, setTablePage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] =
    useState<FeesSwapStatusFilterValue>("all");
  const [detailTx, setDetailTx] = useState<AdminSpreadTransaction | null>(null);
  const [exportOpen, setExportOpen] = useState(false);

  // Backend search is exact-match on ids/asset, so don't fire per keystroke.
  useEffect(() => {
    const id = window.setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  const dateRangeInvalid = Boolean(startDate && endDate) && startDate > endDate;

  const queryDates = useMemo(() => {
    if (startDate && endDate && !dateRangeInvalid) {
      return { startDate, endDate };
    }
    return {};
  }, [startDate, endDate, dateRangeInvalid]);

  const { data, isPending, isError, error, refetch, isFetching } =
    useAdminSpreadSummariesQuery({
      ...queryDates,
      enabled: !dateRangeInvalid,
    });

  const swapStatus = statusFilter === "all" ? undefined : statusFilter;
  const exportContext = useMemo(
    () => ({ type: "spread" as const, swapStatus }),
    [swapStatus]
  );

  const {
    data: txPayload,
    isPending: txPending,
    isError: txError,
    error: txQueryError,
    refetch: refetchTx,
    isFetching: txFetching,
  } = useAdminSpreadTransactionsDataQuery({
    page: tablePage,
    search,
    limit: FEES_TABLE_LIMIT,
    ...queryDates,
    status: swapStatus,
    enabled: !dateRangeInvalid,
  });

  const tableRows = useMemo(
    () => buildFeesTableRows(txPayload?.transactions ?? []),
    [txPayload?.transactions]
  );

  const openDetail = useCallback((row: FeesTableRow) => {
    setDetailTx(row.raw);
  }, []);

  const columns = useMemo(
    () => createFeesTableColumns(openDetail),
    [openDetail]
  );

  const pagination = txPayload?.pagination;
  const tableTotalPages = Math.max(1, pagination?.totalPages ?? 1);
  const tableTotalCount = pagination?.totalCount ?? 0;

  useEffect(() => {
    setTablePage(1);
  }, [startDate, endDate, dateRangeInvalid, statusFilter, search]);

  const flowRows = useMemo(() => {
    if (!data?.flowBreakdown) return [];
    return Object.entries(data.flowBreakdown)
      .map(([key, entry]) => ({ key, label: feesFlowLabel(key), entry }))
      .sort((a, b) => b.entry.totalSpreadUSDT - a.entry.totalSpreadUSDT);
  }, [data?.flowBreakdown]);

  const swapStatusRows = useMemo(() => {
    if (!data?.swapStatusBreakdown) return [];
    const rank = (k: string) => {
      const i = SWAP_STATUS_ORDER.indexOf(k.toUpperCase());
      return i === -1 ? SWAP_STATUS_ORDER.length : i;
    };
    return Object.entries(data.swapStatusBreakdown)
      .map(([key, entry]) => ({ key, entry }))
      .sort(
        (a, b) => rank(a.key) - rank(b.key) || b.entry.count - a.entry.count
      );
  }, [data?.swapStatusBreakdown]);

  const totalTrxReceived = useMemo(
    () =>
      swapStatusRows.reduce((sum, { entry }) => sum + (entry.totalTrx || 0), 0),
    [swapStatusRows]
  );

  const house = data?.houseTrx;

  return (
    <>
      <div className="flex h-screen w-full flex-col overflow-hidden">
        <div className="flex h-[31px] w-full shrink-0 items-center justify-center bg-[#1B1B1B]">
          {IS_STAGING ? (
            <h4 className="text-[12px] font-medium text-[#AEBCC6]">
              You are in Staging:{" "}
              <span>
                <a href={PROD_LINK} className="text-[#03C668]">
                  Switch to Prod
                </a>
              </span>
            </h4>
          ) : (
            <h4 className="text-[12px] font-medium text-[#AEBCC6]">
              You are in PROD:{" "}
              <span>
                <a href={STAGING_LINK} className="text-[#DF4E4E]">
                  Switch to Staging
                </a>
              </span>
            </h4>
          )}
        </div>
        <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden lgss:flex-row">
          <Sidebar />
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto bg-[#13181B] lgss:w-10/12">
            <Navbar />
            <div className="mx-auto w-full max-w-[1600px] flex-1 bg-[#13181B] px-5 pb-24 pt-6 font-manrope mds:px-8 lgss:px-10">
              <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <h1 className="font-sora text-xl font-semibold tracking-tight text-white">
                    Fees
                  </h1>
                  <p className="mt-1 max-w-xl text-[13px] leading-relaxed text-[#869AA9]">
                    Spread taken on swaps, in-app sells and direct sells, and
                    the auto-swap of each fee from USDT to TRX on Obiex. Leave
                    dates empty for all-time totals.
                  </p>
                </div>

                <div
                  className="w-full rounded-xl border border-[#2C363E] bg-[#161E25]/80 p-4 shadow-[0_4px_24px_rgba(0,0,0,0.25)] lg:w-auto lg:min-w-[320px]"
                  role="group"
                  aria-label="Date range filter"
                >
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-[#869AA9]">
                    Date range
                  </p>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <div className="min-w-0 flex-1">
                      <label
                        htmlFor="fees-summary-start"
                        className="mb-1 block text-[11px] text-[#5C6B76]"
                      >
                        From
                      </label>
                      <input
                        id="fees-summary-start"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#1C2227] px-3 text-[13px] text-[#E7EBEE] shadow-inner outline-none transition focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <label
                        htmlFor="fees-summary-end"
                        className="mb-1 block text-[11px] text-[#5C6B76]"
                      >
                        To
                      </label>
                      <input
                        id="fees-summary-end"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#1C2227] px-3 text-[13px] text-[#E7EBEE] shadow-inner outline-none transition focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setStartDate("");
                        setEndDate("");
                      }}
                      className="h-10 shrink-0 rounded-lg border border-[#2C363E] px-4 text-[12px] font-semibold text-[#AEBCC6] transition hover:border-[#3A4852] hover:bg-[#273037] hover:text-white active:scale-[0.98]"
                    >
                      Reset
                    </button>
                  </div>
                  {(startDate || endDate) && !(startDate && endDate) && (
                    <p className="mt-2 text-[11px] text-[#5C6B76]">
                      Select both dates to filter; otherwise all-time data is
                      shown.
                    </p>
                  )}
                </div>
              </header>

              {dateRangeInvalid && (
                <div
                  className="mb-6 rounded-xl border border-amber-500/35 bg-amber-500/[0.07] px-5 py-8 text-center"
                  role="alert"
                >
                  <p className="text-[14px] font-semibold text-amber-200/95">
                    Invalid date range
                  </p>
                  <p className="mx-auto mt-2 max-w-md text-[13px] leading-relaxed text-[#AEBCC6]">
                    Choose an end date on or after the start date, or reset the
                    filter.
                  </p>
                </div>
              )}

              {!dateRangeInvalid && isPending && (
                <div
                  className="flex flex-col items-center justify-center gap-4 rounded-xl border border-[#2C363E] bg-[#161E25]/40 py-24"
                  aria-busy="true"
                >
                  <TailSpin color="#0B75C2" height={40} width={40} />
                  <p className="text-[13px] text-[#869AA9]">
                    Loading fee summary…
                  </p>
                </div>
              )}

              {!dateRangeInvalid && isError && (
                <div className="flex flex-col gap-3 rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-4 text-[13px] text-red-100/95 sm:flex-row sm:items-center sm:justify-between">
                  <span>
                    {apiErrorMessage(error, "Could not load fee summary.")}
                  </span>
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="shrink-0 rounded-lg border border-red-500/40 bg-red-500/20 px-4 py-2 text-[12px] font-semibold text-red-200 transition hover:bg-red-500/30"
                  >
                    Retry
                  </button>
                </div>
              )}

              {!dateRangeInvalid && !isPending && !isError && data && (
                <>
                  {isFetching && !isPending && (
                    <div className="mb-4 flex items-center gap-2 text-[12px] text-[#869AA9]">
                      <span
                        className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-[#869AA9] border-t-transparent"
                        aria-hidden
                      />
                      Refreshing…
                    </div>
                  )}

                  <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-5">
                    <section
                      className="rounded-2xl border border-[#2C363E] bg-gradient-to-b from-[#1C2227] to-[#181D22] p-5 shadow-[0_8px_32px_rgba(0,0,0,0.28)] sm:p-7 lg:col-span-3"
                      aria-labelledby="fees-totals-heading"
                    >
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className="rounded-full border border-[#3A4852] bg-[#0D1114]/80 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#AEBCC6]">
                          USDT
                        </span>
                      </div>
                      <p
                        id="fees-totals-heading"
                        className="text-[11px] font-bold uppercase tracking-widest text-[#869AA9]"
                      >
                        Fees earned
                      </p>
                      <div className="mt-4">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#617889]">
                          Total spread (USDT)
                        </p>
                        <p className="mt-1 font-sora text-2xl font-bold tabular-nums text-[#BEE1FB] sm:text-3xl">
                          {formatAmount(data.totalSpreadUSDT)} USDT
                        </p>
                      </div>
                      <p className="mt-4 text-[13px] text-[#869AA9]">
                        <span className="font-semibold text-[#E7EBEE]">
                          {data.totalCount.toLocaleString()}
                        </span>{" "}
                        trades took a fee in this range
                        {totalTrxReceived > 0 && (
                          <>
                            <span className="mx-2 text-[#5C6B76]">·</span>
                            <span className="font-semibold text-[#E7EBEE]">
                              {formatAmount(totalTrxReceived)} TRX
                            </span>{" "}
                            received from swaps
                          </>
                        )}
                      </p>
                    </section>

                    <section
                      className="rounded-2xl border border-[#2C363E] bg-[#161E25]/60 p-5 sm:p-6 lg:col-span-2"
                      aria-labelledby="fees-house-heading"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <p
                          id="fees-house-heading"
                          className="text-[11px] font-bold uppercase tracking-widest text-[#869AA9]"
                        >
                          House TRX position
                        </p>
                        <span className="rounded-full border border-[#3A4852] bg-[#0D1114]/80 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#AEBCC6]">
                          Live
                        </span>
                      </div>
                      <p className="mt-1 text-[11px] text-[#5C6B76]">
                        Current balance; not affected by the date range.
                      </p>
                      {!house ? (
                        <p className="mt-6 text-[13px] text-[#869AA9]">
                          No house position returned.
                        </p>
                      ) : (
                        <>
                          <p className="mt-4 font-sora text-2xl font-bold tabular-nums text-white">
                            {trxValue(house.houseTrx)}
                          </p>
                          <dl className="mt-4 space-y-2 text-[12px]">
                            <div className="flex items-center justify-between gap-4">
                              <dt className="text-[#7D8F9C]">
                                Obiex available
                              </dt>
                              <dd className="tabular-nums text-[#E7EBEE]">
                                {trxValue(house.obiexTrx)}
                              </dd>
                            </div>
                            <div className="flex items-center justify-between gap-4">
                              <dt className="text-[#7D8F9C]">
                                Less users' TRX
                              </dt>
                              <dd className="tabular-nums text-[#E7EBEE]">
                                {trxValue(house.usersTrx)}
                              </dd>
                            </div>
                          </dl>
                          {house.error && (
                            <p
                              className="mt-4 rounded-lg border border-amber-500/35 bg-amber-500/[0.07] px-3 py-2 text-[12px] text-amber-200/95"
                              role="status"
                            >
                              Obiex unavailable: {house.error}
                            </p>
                          )}
                        </>
                      )}
                    </section>
                  </div>

                  <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
                    <section className="overflow-hidden rounded-xl border border-[#2C363E] bg-[#161E25]/40">
                      <div className="border-b border-[#2C363E] bg-[#1C2227]/80 px-4 py-3">
                        <h3 className="text-[13px] font-bold text-white">
                          By flow
                        </h3>
                        <p className="mt-0.5 text-[11px] text-[#5C6B76]">
                          Where the fees came from
                        </p>
                      </div>
                      {flowRows.length === 0 ? (
                        <p className="px-4 py-10 text-center text-[13px] text-[#869AA9]">
                          No fees in this range.
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[420px] text-left text-[13px]">
                            <thead className="bg-[#0D1114]/80 text-[11px] font-semibold uppercase tracking-wide text-[#869AA9]">
                              <tr>
                                <th className="px-4 py-2.5">Flow</th>
                                <th className="px-4 py-2.5 text-right">
                                  Trades
                                </th>
                                <th className="px-4 py-2.5 text-right">
                                  Fees (USDT)
                                </th>
                                <th className="px-4 py-2.5 text-right">
                                  Share
                                </th>
                              </tr>
                            </thead>
                            <tbody className="text-[#E7EBEE]">
                              {flowRows.map(({ key, label, entry }) => (
                                <tr
                                  key={key}
                                  className="border-t border-[#2C363E] bg-[#13181B]/40"
                                >
                                  <td className="px-4 py-3 font-medium text-white">
                                    {label}
                                  </td>
                                  <td className="px-4 py-3 text-right tabular-nums">
                                    {entry.count.toLocaleString()}
                                  </td>
                                  <td className="px-4 py-3 text-right tabular-nums text-[#BEE1FB]">
                                    {formatAmount(entry.totalSpreadUSDT)}
                                  </td>
                                  <td className="px-4 py-3 text-right tabular-nums text-[#869AA9]">
                                    {data.totalSpreadUSDT > 0
                                      ? `${(
                                          (entry.totalSpreadUSDT /
                                            data.totalSpreadUSDT) *
                                          100
                                        ).toFixed(1)}%`
                                      : "—"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </section>

                    <section className="overflow-hidden rounded-xl border border-[#2C363E] bg-[#161E25]/40">
                      <div className="border-b border-[#2C363E] bg-[#1C2227]/80 px-4 py-3">
                        <h3 className="text-[13px] font-bold text-white">
                          USDT → TRX swap status
                        </h3>
                        <p className="mt-0.5 text-[11px] text-[#5C6B76]">
                          How much of the fees has been converted to TRX
                        </p>
                      </div>
                      {swapStatusRows.length === 0 ? (
                        <p className="px-4 py-10 text-center text-[13px] text-[#869AA9]">
                          No swap activity in this range.
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[420px] text-left text-[13px]">
                            <thead className="bg-[#0D1114]/80 text-[11px] font-semibold uppercase tracking-wide text-[#869AA9]">
                              <tr>
                                <th className="px-4 py-2.5">Status</th>
                                <th className="px-4 py-2.5 text-right">
                                  Trades
                                </th>
                                <th className="px-4 py-2.5 text-right">
                                  Fees (USDT)
                                </th>
                                <th className="px-4 py-2.5 text-right">TRX</th>
                              </tr>
                            </thead>
                            <tbody className="text-[#E7EBEE]">
                              {swapStatusRows.map(({ key, entry }) => (
                                <tr
                                  key={key}
                                  className="border-t border-[#2C363E] bg-[#13181B]/40"
                                >
                                  <td className="px-4 py-3">
                                    <span
                                      className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${swapStatusBadgeClass(
                                        key
                                      )}`}
                                    >
                                      {key === "undefined" ? "Unknown" : key}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-right tabular-nums">
                                    {entry.count.toLocaleString()}
                                  </td>
                                  <td className="px-4 py-3 text-right tabular-nums text-[#BEE1FB]">
                                    {formatAmount(entry.totalSpreadUSDT)}
                                  </td>
                                  <td className="px-4 py-3 text-right tabular-nums">
                                    {entry.totalTrx
                                      ? formatAmount(entry.totalTrx)
                                      : "—"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </section>
                  </div>
                </>
              )}

              {!dateRangeInvalid && (
                <>
                  <div className="w-full flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mt-4 mb-3">
                    <h4 className="text-white font-sora font-semibold text-[12px] shrink-0">
                      Activity
                    </h4>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      <button
                        type="button"
                        onClick={() => setExportOpen(true)}
                        className="flex items-center justify-center gap-2 cursor-pointer opacity-90 hover:opacity-100"
                        aria-label="Export fee summary and transactions"
                      >
                        <BiExport className="text-white text-lg" aria-hidden />
                        <span className="uppercase font-sora font-semibold text-[#869AA9] text-[10px] mt-0.5">
                          Export
                        </span>
                      </button>
                    </div>
                  </div>
                  <section
                    id="fee-transactions"
                    className="mt-10 border-t border-[#2C363E]/80 pt-10"
                  >
                    <div className="mb-4">
                      <h3 className="font-sora text-[15px] font-bold text-white">
                        Fee transactions
                      </h3>
                      <p className="mt-0.5 text-[12px] text-[#5C6B76]">
                        One row per trade that took a fee, newest first.
                      </p>
                    </div>

                    <div
                      className="mb-4 flex flex-wrap gap-2"
                      role="tablist"
                      aria-label="TRX swap status filter"
                    >
                      {FEES_SWAP_STATUS_FILTERS.map((opt) => {
                        const active = statusFilter === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => setStatusFilter(opt.value)}
                            className={
                              active
                                ? "rounded-full border border-secondary_button bg-secondary_button/15 px-4 py-2 text-[12px] font-bold text-secondary_button"
                                : "rounded-full border border-[#2C363E] bg-[#161E25]/80 px-4 py-2 text-[12px] font-semibold text-[#AEBCC6] transition hover:border-[#3A4852] hover:text-white"
                            }
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mb-4">
                      <input
                        type="search"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Search by exact trade ID, asset (e.g. BTC), Obiex ref or user ID"
                        aria-label="Search fee transactions"
                        className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#161E25]/80 px-3 text-[13px] text-[#E7EBEE] outline-none transition focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
                      />
                    </div>

                    {txFetching && !txPending && (
                      <p className="mb-2 text-[12px] text-[#869AA9]">
                        Updating list…
                      </p>
                    )}

                    {txError && (
                      <div className="mb-4 flex flex-col gap-2 rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-3 text-[13px] text-red-100/95 sm:flex-row sm:items-center sm:justify-between">
                        <span>
                          {apiErrorMessage(
                            txQueryError,
                            "Could not load fee transactions."
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={() => void refetchTx()}
                          className="shrink-0 rounded-lg border border-red-500/40 bg-red-500/20 px-3 py-1.5 text-[12px] font-semibold text-red-200"
                        >
                          Retry
                        </button>
                      </div>
                    )}

                    {txPending && !txError && (
                      <div className="flex justify-center rounded-xl border border-[#2C363E] bg-[#161E25]/40 py-16">
                        <TailSpin color="#0B75C2" height={36} width={36} />
                      </div>
                    )}

                    {!txPending && !txError && (
                      <DataTable<FeesTableRow>
                        columns={columns}
                        data={tableRows}
                        currentPage={tablePage}
                        setCurrentPage={setTablePage}
                        totalPage={tableTotalPages}
                        count={tableTotalCount}
                        limit={FEES_TABLE_LIMIT}
                        emptyContent="No fee transactions for this filter."
                        className="overflow-hidden rounded-xl border border-[#2C363E]"
                        fixedBodyHeight="min(520px, 48vh)"
                      />
                    )}
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <AdminFeeDetailModal
        open={detailTx != null}
        onClose={() => setDetailTx(null)}
        transaction={detailTx}
      />
      <AdminTransactionsExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Fees"
        exportContext={exportContext}
      />
    </>
  );
};

export default FeesOverview;
